import { useState } from 'react';

const SERVICES = [
  'Janitorial Services',
  'Common Area Cleaning',
  'Office Cleaning',
  'Medical Office Cleaning',
  'Post Construction Cleaning',
  'Pressure Washing',
  'Window Washing',
  'Floor Care',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Quote() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/db37pk8m252sfkho289g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('We could not reach the server. Please try again in a moment.');
    }
  };

  return (
    <section id="quote" className="relative overflow-hidden bg-secondary-100 py-16 md:py-24">
      <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-secondary-200/60 blur-2xl"></div>
      <div className="relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-2 lg:gap-16">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
            Free, No-Obligation Estimate
          </span>
          <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight text-primary-800 md:text-4xl">
            Request A Quote
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-700 md:text-base">
            Tell us about your facility and we&apos;ll build a cleaning plan that fits. Our team responds quickly —
            usually the same business day.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              'Tailored cleaning programs for your space',
              'Fully trained, background-checked local staff',
              'Reliable scheduling you can count on',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm font-medium text-foreground-700">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-check-line text-[14px]"></i>
                </span>
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-phone-fill text-xl"></i>
            </span>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-foreground-500">Call us directly</p>
              <a href="tel:2399367700" className="font-heading text-lg font-bold text-primary-700">
                239.936.7700
              </a>
            </div>
          </div>
        </div>

        <form
          data-readdy-form
          id="request-a-quote-form"
          onSubmit={handleSubmit}
          className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label htmlFor="q-name" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                Full Name
              </label>
              <input
                id="q-name"
                name="name"
                type="text"
                required
                placeholder="Jane Doe"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="q-email" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                Email
              </label>
              <input
                id="q-email"
                name="email"
                type="email"
                required
                placeholder="you@company.com"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="q-phone" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                Phone
              </label>
              <input
                id="q-phone"
                name="phone"
                type="tel"
                placeholder="239.000.0000"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="q-service" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                Service Needed
              </label>
              <select
                id="q-service"
                name="service"
                defaultValue=""
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              >
                <option value="" disabled>
                  Select a service
                </option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="q-city" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                City
              </label>
              <input
                id="q-city"
                name="city"
                type="text"
                placeholder="Fort Myers"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="q-message" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                Tell us about your space
              </label>
              <textarea
                id="q-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="Square footage, frequency, and anything else we should know…"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-accent-500"
              />
            </div>
          </div>

          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />

          {status === 'success' && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900">
              <i className="ri-checkbox-circle-fill text-accent-600"></i> Thank you! Your request has been received — we&apos;ll
              be in touch shortly.
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-200 px-4 py-3 text-sm font-medium text-foreground-800">
              <i className="ri-error-warning-fill text-primary-600"></i> {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-60"
          >
            {status === 'submitting' ? (
              <>
                <i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill text-[18px]"></i> Send My Request
              </>
            )}
          </button>
          <p className="mt-3 text-center text-xs text-foreground-500">
            We respect your privacy. Your details are only used to respond to your request.
          </p>
        </form>
      </div>
    </section>
  );
}